import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import React, { useState, useEffect, useRef, Children } from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { CONTINUOUS_SCROLL_LOAD_STRATEGIES, DEFAULT_ANIMATION_DURATION_SECONDS, DEFAULT_CONTAINER_CLASS_NAME, DEFAULT_INTERSECTION_OBSERVER_ROOT_MARGIN, DEFAULT_INTERSECTION_OBSERVER_THRESHOLD, EAGER_LOAD_STRATEGY, IN_VIEWPORT_LOAD_STRATEGY, DEFAULT_MULTIPLIER, RESTART_ANIMATION_INTERVAL_FACTOR, SECONDS_TO_MILLISECONDS } from './ContinuousScroll.constants';
import { DeviceSettingsPropType } from './ContinuousScroll.types';
import { getDeviceSetting, getContainerStyle, getElementStyles, isUsingCustomWidths, getAnimationKeyFrames, getGlobalLeftOffset } from './ContinuousScroll.utils';
import ContinuousScrollItem from './components/ContinuousScrollItem';
import useResponsive from '../../../../hooks/use-responsive/use-responsive';
import { classNames } from '../../../../utils/classname';
import useIntersectionObserver from '../../../foundation/hooks/use-intersection-observer/use-intersection-observer';
import { createComponent } from '../../../foundation/services/components';
import { ChildrenPropType } from '../../../foundation/types/component';
import { ImageSizePatternPropType } from '../../../images/components/PatternedImageRow/PatternedImageRow.types';
import './ContinuousScroll.less';
var defaultProps = {
  children: null,
  items: null,
  animationDurationSeconds: DEFAULT_ANIMATION_DURATION_SECONDS,
  multiplier: DEFAULT_MULTIPLIER,
  className: '',
  loadStrategy: EAGER_LOAD_STRATEGY,
  deviceSettings: {},
  sizePatterns: null
};
export default function ContinuousScroll(_ref) {
  var _classNames;
  var children = _ref.children,
    items = _ref.items,
    animationDurationSeconds = _ref.animationDurationSeconds,
    multiplier = _ref.multiplier,
    className = _ref.className,
    loadStrategy = _ref.loadStrategy,
    deviceSettings = _ref.deviceSettings,
    sizePatterns = _ref.sizePatterns;
  var containerRef = useRef(null);
  var responsive = useResponsive();
  var elements = [].concat(_toConsumableArray(items || []), _toConsumableArray(Children.toArray(children || [])));
  var finalElements = Array(multiplier).fill(elements).flat();
  var totalElements = finalElements.length;
  var isInViewport = loadStrategy === IN_VIEWPORT_LOAD_STRATEGY;
  var isEager = loadStrategy === EAGER_LOAD_STRATEGY;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    show = _useState2[0],
    setShow = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    restartAnimation = _useState4[0],
    setRestartAnimation = _useState4[1];
  var customWidths = isUsingCustomWidths(sizePatterns);
  var globalLeftOffset = getGlobalLeftOffset(sizePatterns);
  var _useIntersectionObser = useIntersectionObserver(containerRef, {
      defaultActive: isInViewport,
      threshold: DEFAULT_INTERSECTION_OBSERVER_THRESHOLD,
      rootMargin: DEFAULT_INTERSECTION_OBSERVER_ROOT_MARGIN
    }),
    inViewport = _useIntersectionObser.inViewport,
    disconnect = _useIntersectionObser.disconnect;
  var deviceSetting = getDeviceSetting(deviceSettings, responsive);
  var gap = deviceSetting.gap;
  useEffect(function () {
    var interval = null;
    if (customWidths) {
      interval = setInterval(function () {
        setRestartAnimation(function (previousValue) {
          return previousValue + 1;
        });
      }, animationDurationSeconds * RESTART_ANIMATION_INTERVAL_FACTOR * SECONDS_TO_MILLISECONDS);
    }
    return function () {
      return clearInterval(interval);
    };
  }, [restartAnimation]);
  useEffect(function () {
    if (inViewport) {
      setShow(true);
      disconnect();
    }
  }, [inViewport]);
  useEffect(function () {
    var keyframes = getAnimationKeyFrames({
      totalElements: totalElements,
      gap: gap
    });
    var style = document.createElement('style');
    style.innerHTML = keyframes;
    document.head.appendChild(style);
    if (isEager) {
      setShow(true);
    }
    return function () {
      document.head.removeChild(style);
    };
  }, [totalElements, gap]);
  if (_isEmpty(finalElements)) {
    return null;
  }
  var containerClassName = classNames((_classNames = {}, _defineProperty(_classNames, DEFAULT_CONTAINER_CLASS_NAME, true), _defineProperty(_classNames, className, true), _classNames));
  var containerStyle = getContainerStyle({
    totalElements: totalElements,
    deviceSetting: deviceSetting
  });
  return /*#__PURE__*/React.createElement("div", {
    className: containerClassName,
    ref: containerRef,
    style: containerStyle
  }, show && finalElements.map(function (element, index) {
    var _getElementStyles = getElementStyles({
        duration: animationDurationSeconds,
        totalElements: totalElements,
        index: index,
        patterns: sizePatterns,
        globalLeftOffset: globalLeftOffset
      }),
      parentStyle = _getElementStyles.parentStyle,
      childStyle = _getElementStyles.childStyle;
    var child = _get(element, 'props.children[0]');
    var elementProps = {
      key: "".concat(index, "-").concat(restartAnimation),
      style: parentStyle
    };
    if (!_isEmpty(child)) {
      elementProps = _objectSpread(_objectSpread({}, elementProps), {}, {
        children: createComponent(child, {
          style: childStyle
        })
      });
    }
    return createComponent(element, elementProps);
  }));
}
ContinuousScroll.Item = ContinuousScrollItem;
ContinuousScroll.defaultProps = defaultProps;